import React, { useMemo, useState, useCallback } from "react";
import { format } from "date-fns";
import {
  makeStyles,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Link,
  IconButton,
} from "@material-ui/core";
import { ChevronLeft, ChevronRight } from "@material-ui/icons";

export default function Post({ post, source = 'facebook' }) {
  const classes = useStyles();
  const isMiniAlbum = post?.type === "mini_album" && Array.isArray(post?.items);
  const [albumIndex, setAlbumIndex] = useState(0);

  const albumLength = isMiniAlbum ? post.items.length : 0;
  const isInstagram = source === 'instagram';

  const goPrev = useCallback(() => {
    if (isMiniAlbum && albumLength > 0) {
      setAlbumIndex((i) => i > 0 ? i - 1 : albumLength - 1);
    }
  }, [isMiniAlbum, albumLength]);

  const goNext = useCallback(() => {
    if (isMiniAlbum && albumLength > 0) {
      setAlbumIndex((i) => i < albumLength - 1 ? i + 1 : 0);
    }
  }, [isMiniAlbum, albumLength]);

  const formatDate = (date) => {
    if (!date) return "Data não disponível";
    const parsed = new Date(date);
    if (isNaN(parsed.getTime())) return "Data inválida";
    return format(parsed, "dd/MM/yyyy");
  };

  const isVideo = isInstagram
    ? post?.mediaType === "VIDEO" || /video/i.test(post?.mediaType || "")
    : post?.type === "video" ||
      /video/i.test(post?.mimeType || "") ||
      (!!post?.source && /\.mp4|\.mov|\.webm/i.test(post.source));

  const videoUrl = isInstagram
    ? post?.mediaUrl || ""
    : post?.source || post?.videoUrl || post?.media?.source || "";

  const poster = isInstagram
    ? post?.thumbnailUrl || ""
    : post?.thumbnail ||
      post?.full_picture ||
      post?.picture ||
      post?.coverPhoto ||
      "";

  const imageUrl = isInstagram
    ? post?.mediaUrl || ""
    : post?.source || post?.picture || post?.coverPhoto || "";

  const miniAlbumCurrentImage = useMemo(() => {
    if (!isMiniAlbum) return "";
    const item = post.items[albumIndex] || {};
    return item.source || item.picture || "";
  }, [isMiniAlbum, post, albumIndex]);

  const createdAt = isInstagram
    ? post?.timestamp || post?.createdAt
    : post?.createdTime || post?.created_date;

  const originUrl = isInstagram
    ? post?.permalink || ""
    : post?.permalink_url || post?.permalink || post?.link || "";

  const description = isInstagram
    ? post?.caption || ""
    : post?.description || "";

  const sourceName = isInstagram
    ? "Instagram"
    : post?.sourceName || "Facebook";

  return (
    <Card className={classes.card} elevation={3}>
      {/* Mídia */}
      {isMiniAlbum ? (
        <div className={classes.playerAlbum}>
          {miniAlbumCurrentImage ? (
            <img
              src={miniAlbumCurrentImage}
              alt={description || post?.items?.[albumIndex]?.name || "Mini álbum"}
              className={classes.image}
              loading="lazy"
            />
          ) : (
            <Box className={classes.empty}>Imagem não disponível</Box>
          )}

          {/* Contador de fotos */}
          {albumLength > 1 && (
            <div className={classes.photoCounter}>
              {albumIndex + 1} de {albumLength} fotos
            </div>
          )}

          {/* Botões de navegação - sempre mostrar se houver mais de 1 foto */}
          {albumLength > 1 && (
            <>
              <IconButton
                className={`${classes.navButton} ${classes.navLeft}`}
                aria-label="Foto anterior"
                onClick={goPrev}
              >
                <ChevronLeft />
              </IconButton>

              <IconButton
                className={`${classes.navButton} ${classes.navRight}`}
                aria-label="Próxima foto"
                onClick={goNext}
              >
                <ChevronRight />
              </IconButton>
            </>
          )}
        </div>
      ) : isVideo && videoUrl ? (
        <div className={classes.player}>
          <video
            className={classes.video}
            src={videoUrl}
            poster={poster}
            controls
            preload="metadata"
            playsInline
          >
            Seu navegador não suporta vídeo HTML5.
          </video>
        </div>
      ) : imageUrl ? (
        <img
          className={classes.image}
          src={imageUrl}
          alt={description || "Publicação"}
          loading="lazy"
        />
      ) : post?.embedHtml ? (
        <div
          className={classes.player}
          // fallback para embeds (ex.: Facebook)
          dangerouslySetInnerHTML={{ __html: post.embedHtml }}
        />
      ) : (
        <Box className={classes.empty}>Mídia não disponível</Box>
      )}

      {/* Conteúdo */}
      <CardContent className={classes.content}>
        <Box className={classes.header}>
          <Typography variant="caption" className={classes.date}>
            {`Publicado em ${formatDate(createdAt)}`}
          </Typography>
        </Box>

        {description && (
          <Typography
            variant="body1"
            className={classes.description}
            title={description}
          >
            {description}
          </Typography>
        )}

        {sourceName && (
          <Box mt={1} className={classes.footer}>
            <Chip
              label={sourceName}
              size="small"
              className={classes.badge}
            />
            {originUrl && (
              <Link
                href={originUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={classes.footerLink}
              >
                Ver publicação original
              </Link>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

const useStyles = makeStyles((theme) => ({
  card: {
    maxWidth: 820,
    width: "92vw",
    margin: "0 auto",
    borderRadius: 16,
    overflow: "hidden",
    background:
      (theme.colors && theme.colors.white) ||
      theme.palette.background.paper,
    boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
  },

  /* PLAYER 16:9 */
  player: {
    position: "relative",
    width: "100%",
    paddingTop: "56.25%", // 16:9
    backgroundColor:
      (theme.colors && theme.colors.darkBlue) || "#000",
  },
  playerAlbum: {
    position: "relative",
    width: "100%",
    backgroundColor:
      (theme.colors && theme.colors.darkBlue) || "#000",
  },
  video: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "contain",
    outline: "none",
    border: 0,
    display: "block",
  },

  image: {
    display: "block",
    width: "100%",
    height: "auto",
  },

  albumImage: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "contain",
    backgroundColor: "#000",
  },

  navButton: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    backgroundColor: "rgba(0,0,0,0.45)",
    color: "#fff",
    '&:hover': {
      backgroundColor: "rgba(0,0,0,0.6)",
    },
  },
  navLeft: {
    left: 8,
  },
  navRight: {
    right: 8,
  },

  photoCounter: {
    position: "absolute",
    top: 8,
    right: 8,
    backgroundColor: "rgba(0,0,0,0.7)",
    color: "#fff",
    padding: "4px 8px",
    borderRadius: 4,
    fontSize: 12,
    fontFamily: "Karla, sans-serif",
    fontWeight: 500,
    zIndex: 2,
  },

  empty: {
    padding: theme.spacing(6),
    textAlign: "center",
    color:
      (theme.colors && theme.colors.gray1) ||
      theme.palette.text.secondary,
  },

  content: {
    padding: theme.spacing(2.5),
    [theme.breakpoints.up("sm")]: {
      padding: theme.spacing(3),
    },
  },

  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  date: {
    fontFamily: "Karla, sans-serif",
    fontSize: 14,
    color:
      (theme.colors && theme.colors.gray1) ||
      theme.palette.text.secondary,
  },
  badge: {
    backgroundColor:
      (theme.colors && theme.colors.darkBlue) || theme.palette.primary.main,
    color:
      (theme.colors && theme.colors.white) ||
      theme.palette.primary.contrastText,
    height: 22,
  },

  description: {
    marginTop: theme.spacing(1),
    fontFamily: "Karla, sans-serif",
    fontSize: 18,
    lineHeight: 1.45,
    color:
      (theme.colors && theme.colors.gray) ||
      theme.palette.text.primary,
    display: "-webkit-box",
    WebkitLineClamp: 6,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    wordBreak: "break-word",
  },

  footer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "nowrap",
  },

  footerLink: {
    fontWeight: 500,
    fontSize: 12,
    textDecoration: "none",
    color: (theme.colors && theme.colors.gray1) || theme.palette.text.secondary,
    whiteSpace: "nowrap",
    "&:hover": {
      textDecoration: "underline",
      color: (theme.colors && theme.colors.darkBlue) || theme.palette.primary.main,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 11,
    }
  },

  link: {
    fontWeight: 600,
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" },
  },
}));
